import { assign, assignWith } from "lodash";
export class ConfigurationManager {
    // input configs should be top-to-bottom, e.g. unit, investigation, problem
    constructor(defaults, configs) {
        Object.defineProperty(this, "defaults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "configs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.defaults = defaults;
        // reverse the array so searches are bottom-to-top, e.g. problem, investigation, unit
        this.configs = [...configs].reverse();
    }
    getProp(prop) {
        var _a;
        const found = (_a = this.configs.find(config => config[prop] != null)) === null || _a === void 0 ? void 0 : _a[prop];
        return (found != null ? found : this.defaults[prop]);
    }
    /*
     * UnitConfiguration properties
     */
    get appName() {
        return this.getProp("appName");
    }
    get pageTitle() {
        return this.getProp("pageTitle");
    }
    get demoProblemTitle() {
        return this.getProp("demoProblemTitle");
    }
    get defaultProblemOrdinal() {
        return this.getProp("defaultProblemOrdinal");
    }
    get showCommentTag() {
        return this.getProp("showCommentTag");
    }
    get showCommentRating() {
        return this.getProp("showCommentRating");
    }
    get commentTags() {
        return (this.showCommentTag) ? this.getProp("commentTags") : {};
    }
    get allowCustomCommentTags() {
        return this.showCommentTag ? this.getProp("allowCustomCommentTags") : false;
    }
    get aiEvaluation() {
        return this.getProp("aiEvaluation");
    }
    get aiPrompt() {
        return this.getProp("aiPrompt");
    }
    get autoAssignStudentsToIndividualGroups() {
        return this.getProp("autoAssignStudentsToIndividualGroups");
    }
    get defaultDocumentType() {
        return this.getProp("defaultDocumentType");
    }
    get defaultDocumentTitle() {
        return this.getProp("defaultDocumentTitle");
    }
    get docTimeStampPropertyName() {
        return this.getProp("docTimeStampPropertyName");
    }
    get docDisplayIdPropertyName() {
        return this.getProp("docDisplayIdPropertyName");
    }
    get defaultLearningLogTitle() {
        return this.getProp("defaultLearningLogTitle");
    }
    get initialLearningLogTitle() {
        return this.getProp("initialLearningLogTitle");
    }
    get defaultLearningLogDocument() {
        return this.getProp("defaultLearningLogDocument");
    }
    get autoSectionProblemDocuments() {
        return this.getProp("autoSectionProblemDocuments");
    }
    get documentLabelProperties() {
        return this.getProp("documentLabelProperties");
    }
    get documentLabels() {
        return this.getProp("documentLabels");
    }
    get disablePublish() {
        return this.getProp("disablePublish");
    }
    get enableHistoryRoles() {
        return this.getProp("enableHistoryRoles");
    }
    get enableCommentRoles() {
        return this.getProp("enableCommentRoles");
    }
    get copyPreferOriginTitle() {
        return this.getProp("copyPreferOriginTitle");
    }
    get disableTileDrags() {
        return this.getProp("disableTileDrags");
    }
    get showClassSwitcher() {
        return this.getProp("showClassSwitcher");
    }
    get supportStackedTwoUpView() {
        return this.getProp("supportStackedTwoUpView");
    }
    get showPublishedDocsInPrimaryWorkspace() {
        return this.getProp("showPublishedDocsInPrimaryWorkspace");
    }
    get comparisonPlaceholderContent() {
        return this.getProp("comparisonPlaceholderContent");
    }
    get initiallyHideExemplars() {
        return this.getProp("initiallyHideExemplars");
    }
    get navTabs() {
        return this.getProp("navTabs");
    }
    /*
     * ProblemConfiguration properties
     */
    get disabledFeatures() {
        // settings are merged rather than simply returning the closest non-empty value
        const reverseConfigs = [...this.configs].reverse();
        const mergedDisabled = {};
        mergeDisabledFeatures(mergedDisabled, this.defaults.disabledFeatures);
        for (const config of reverseConfigs) {
            mergeDisabledFeatures(mergedDisabled, config.disabledFeatures);
        }
        return Object.values(mergedDisabled);
    }
    get toolbar() {
        return this.getProp("toolbar");
    }
    get annotations() {
        return this.getProp("annotations");
    }
    get authorTools() {
        return this.getProp("authorTools");
    }
    get myResourcesToolbar() {
        return this.getProp("myResourcesToolbar");
    }
    get tools() {
        return this.getProp("tools");
    }
    get defaultDocumentTemplate() {
        return this.getProp("defaultDocumentTemplate");
    }
    get planningTemplate() {
        return this.getProp("planningTemplate");
    }
    get placeholder() {
        return this.getProp("placeholder");
    }
    get placeholderText() {
        return this.getProp("placeholderText");
    }
    get stamps() {
        return this.getProp("stamps");
    }
    get showIdeasButton() {
        return this.getProp("showIdeasButton");
    }
    get groupDocumentsEnabled() {
        return this.getProp("groupDocumentsEnabled");
    }
    get hide4up() {
        return this.getProp("hide4up");
    }
    get sortWorkConfig() {
        return this.getProp("sortWorkConfig");
    }
    get termOverrides() {
        return this.getProp("termOverrides");
    }
    get defaultPanelLayout() {
        return this.getProp("defaultPanelLayout");
    }
    get defaultSharedDocuments() {
        return this.getProp("defaultSharedDocuments");
    }
    get classWideDocuments() {
        return this.getProp("classWideDocuments");
    }
    get settings() {
        // settings are merged rather than simply returning the closest non-empty value
        // merge is just two levels deep: eg merges items under settings.table, but not elements of settings.table.tools
        const reverseSettings = [...this.configs].reverse().map(config => config.settings);
        return assignWith({}, this.defaults.settings, ...reverseSettings, (objValue, srcValue) => {
            return assign({}, objValue, srcValue);
        });
    }
}
export function mergeDisabledFeatures(disabled, disabledFeatures) {
    disabledFeatures === null || disabledFeatures === void 0 ? void 0 : disabledFeatures.forEach(feature => {
        const result = /^!?(.+)/.exec(feature);
        const featureKey = result === null || result === void 0 ? void 0 : result[1];
        featureKey && (disabled[featureKey] = feature);
    });
}
//# sourceMappingURL=data:application/json;base64,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